import { sourceCredentialFetch } from "../util/authenticatedFetch";

/** The longest sign-out waits for the server to end this browser's project sessions. */
const PROJECT_SIGN_OUT_TIMEOUT_MS = 2_000;

/**
 * Sign-out's first step: ai_engine ends every project-URL session exchanged from this browser's
 * sign-in (`POST /session/sign-out`), so a copied tab credential stops working with the sign-out,
 * not up to an hour later. It goes out on that sign-in (the source cookie, or the embed/native
 * source JWT), so it must be sent before the sign-in is revoked. Best effort: an older server
 * answers 404, a provider outage 503, a lost network nothing at all; none of them stops the local
 * sign-out, which waits no longer than `PROJECT_SIGN_OUT_TIMEOUT_MS`. Never rejects.
 */
export async function endProjectSessions(): Promise<void> {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<"timed out">((resolve) => {
        timer = setTimeout(() => {
            controller.abort();
            resolve("timed out");
        }, PROJECT_SIGN_OUT_TIMEOUT_MS);
    });
    const request = sourceCredentialFetch("/api/ai/session/sign-out", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
        signal: controller.signal,
    });
    try {
        const outcome = await Promise.race([request, timedOut]);
        if (outcome === "timed out")
            console.warn("[Logout] Project sessions: no answer in time; signing out here anyway");
        else if (!outcome.ok)
            console.warn(`[Logout] Project sessions were not ended (${outcome.status})`);
    } catch (error) {
        console.warn("[Logout] Project sessions were not ended:", error);
    } finally {
        clearTimeout(timer);
    }
}
